import React, { FC } from 'react';
import classNames from 'classnames';
import { AccordionTrigger } from './AccordionTrigger';
import { AccordionContent } from './AccordionContent';
import styles from './MobileAccordionMenuItem.scss';
import { NestedMenu } from '../mobile-menu/NestedMenu';
import { notifyAnalytics } from '../notify-analytics';

interface MobileAccordionMenuItemProps {
  item: MenuItem;
  index: number;
  isActive: boolean;
  isClosing: boolean;
  onItemClick: () => void;
  onItemOpened: (index: number, name: string) => void;
  onAnimationEnd: () => void;
  onNavigateBack?: () => void;
  hierarchy?: string[];
}

export const MobileAccordionMenuItem: FC<MobileAccordionMenuItemProps> = ({
  item,
  index,
  isActive,
  isClosing,
  onItemClick,
  onItemOpened,
  onAnimationEnd,
  onNavigateBack = () => {},
  hierarchy = [],
}) => {
  const { name: accordionHeading, child } = item;
  const hasChildren = (child?.length ?? 0) > 0;
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);

  const sendAnalytics = (accordionLink: string) => {
    notifyAnalytics([...hierarchy, accordionHeading, accordionLink].join('|'));
  };

  const handleButtonClick = (index: number, linkName: string) => {
    sendAnalytics(linkName);
    if (child[index].child?.length > 0) {
      setActiveIndex(index);
    }
    onItemClick();
  };

  const handleMenuOpen = (index: number, name: string) => {
    notifyAnalytics([...hierarchy, name].join('|'));
    onItemOpened(index, name);
  };

  const onBackClick = () => {
    setActiveIndex(-1);
    onNavigateBack();
  };

  return (
    <li
      data-testid={`accordion-menu-item-${index}`}
      className={classNames(styles.accordionItem, {
        [styles.accordionItem__active]: isActive,
      })}
    >
      <AccordionTrigger
        name={accordionHeading}
        isActive={isActive}
        onClick={() => handleMenuOpen(index, accordionHeading)}
        hasChildren={hasChildren}
      />
      {(isActive || isClosing) && hasChildren && (
        <AccordionContent
          items={child}
          isClosing={isClosing}
          onAnimationEnd={onAnimationEnd}
          onButtonClick={handleButtonClick}
          onLinkClick={sendAnalytics}
        />
      )}
      {activeIndex !== -1 && (
        <NestedMenu
          data={child[activeIndex]}
          onNavigateBack={onBackClick}
          hierarchy={[...hierarchy, accordionHeading]}
        />
      )}
    </li>
  );
};
